import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { resolveAssetUrl } from '../../lib/assets';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ShoppingBag, Trash2, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== LISTINGS TAB ====================
function ListingsTab({ language }) {
  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => { fetchListings(); }, [page, categoryFilter]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params = { skip: page * 20, limit: 20 };
      if (categoryFilter) params.category = categoryFilter;
      const { data } = await axios.get(`${API}/admin/listings`, { params });
      setListings(data.listings);
      setTotal(data.total);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/admin/listings/${id}/toggle`);
      fetchListings();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/listings/${id}`);
      fetchListings();
    } catch (e) { console.error(e); }
  };

  const CATEGORY_COLORS = { shop: 'bg-[#D97736]', service: 'bg-[#4A6B53]', event: 'bg-[#6366f1]', project: 'bg-[#8b5cf6]', business: 'bg-[#0ea5e9]' };
  const categories = ['', 'shop', 'service', 'event', 'project', 'business'];
  const CAT_LABELS = { lv: { shop: 'Veikals', service: 'Pakalpojumi', event: 'Pasākumi', project: 'Projekti', business: 'Uzņēmumi' }, en: { shop: 'Shop', service: 'Services', event: 'Events', project: 'Projects', business: 'Businesses' } };

  return (
    <div data-testid="admin-listings">
      <div className="flex gap-2 mb-6 flex-wrap">
        {categories.map(c => (
          <button
            key={c || 'all'}
            onClick={() => { setCategoryFilter(c); setPage(0); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              categoryFilter === c ? 'bg-[#121212] text-white' : 'bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:border-[#121212]'
            }`}
          >
            {c === '' ? (language === 'lv' ? 'Visi' : 'All') : (CAT_LABELS[language]?.[c] || c)}
          </button>
        ))}
        <span className="text-sm text-[#5C5C5C] self-center ml-auto">{total} {language === 'lv' ? 'kopā' : 'total'}</span>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-white rounded-xl animate-pulse" />)}</div>
      ) : listings.length === 0 ? (
        <div className="text-center py-12 text-[#5C5C5C]">
          <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>{language === 'lv' ? 'Nav sludinājumu' : 'No listings'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {listings.map(listing => (
            <div key={listing.id} className="bg-white border border-[#E5E4E0] rounded-xl p-4 flex items-center gap-4" data-testid={`admin-listing-${listing.id}`}>
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#F3F2EE] flex-shrink-0">
                {listing.images?.[0] && <img src={resolveAssetUrl(listing.images[0])} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-[#121212] truncate">{listing.title}</h3>
                  <Badge className={`${CATEGORY_COLORS[listing.category]} text-white border-0 text-xs`}>{listing.category}</Badge>
                  {!listing.is_active && <Badge variant="outline" className="text-red-500 border-red-500 text-xs">{language === 'lv' ? 'Neaktīvs' : 'Inactive'}</Badge>}
                </div>
                <p className="text-sm text-[#5C5C5C]">{listing.owner_name} | {listing.region}</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" onClick={() => handleToggle(listing.id)} data-testid={`toggle-listing-${listing.id}`}>
                  {listing.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-listing-${listing.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{language === 'lv' ? 'Dzēst sludinājumu?' : 'Delete listing?'}</AlertDialogTitle>
                      <AlertDialogDescription>{listing.title}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{language === 'lv' ? 'Atcelt' : 'Cancel'}</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => handleDelete(listing.id)}>
                        {language === 'lv' ? 'Dzēst' : 'Delete'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ListingsTab;
